import type { Messages } from '../de';

export default {
  page: {
    title: 'Goals & records',
    titleMobile: 'Goals',
    kickerGoals: { one: '{count} active goal', other: '{count} active goals' },
    kickerStreaks: { one: '{count} streak', other: '{count} streaks' },
    addGoal: 'Add goal',
    loadError: 'Goals can’t be loaded right now: {error}',
    yearGoals: 'Goals {year}',
    moreGoals: 'More goals',
    inactiveAria: 'Inactive goals',
    inactive: 'Inactive',
    inactiveFrom: '{name} · from {from}',
    inactiveFromTo: '{name} · from {from} to {to}',
    steps: 'Steps',
    sleep: 'Sleep',
    all: 'All',
    editAria: 'Edit {label}',
    streaks: 'Streaks',
    streaksSub: 'Strict: one missed day or week ends the streak. New rules are calculated retroactively over all data.',
    streaksError: 'Streaks unavailable: {error}',
    addStreak: 'Add streak',
    newStreak: 'New streak',
    recordsError: 'Fastest efforts unavailable: {error}',
    removeStreakConfirm: 'Remove streak “{label}”? You can add it again at any time and it will be recalculated retroactively.',
  },
  unit: {
    day: { one: 'day', other: 'days' },
    week: { one: 'week', other: 'weeks' },
  },
  metric: {
    distance: 'Distance',
    duration: 'Time',
    elevation: 'Elevation',
    count: 'Count',
    steps: 'Steps per day',
    sleep: 'Sleep per night',
  },
  metricUnit: { elevation: 'm', steps: 'steps' },
  goal: {
    perWeek: '{value}/week',
    barAria: '{actual} of {target}',
    barAriaExpected: '{actual} of {target}, expected today {pct}',
    expectedToday: 'Expected today',
    lineLegend: 'Line = expected today',
    pathAria: 'Goal path {label}',
    actual: 'Actual',
    target: 'Plan',
    forecast: 'Forecast',
    tooltipActual: 'Actual: {value}',
    endLabel: 'Goal {target}',
    today: 'today',
    outOfReach: 'At the current pace the goal is out of reach.',
    behind: 'A little more per week and the goal is still doable.',
    adjust: 'Adjust goal',
    orKeep: 'or keep it as a reminder.',
    realistic: 'Realistically reachable',
    neededFromToday: 'Needed from today',
    reached: 'reached',
    forecastTile: 'Forecast (pace of last 8 weeks)',
    ahead: 'Ahead of plan',
    gap: 'Behind plan',
    adjustQ: 'Adjust goal?',
    chartAria: 'Goal path: plan line up to {target}, actual {actual}',
    chartAriaForecast: ', forecast {forecast}',
    pathError: 'Goal path unavailable: {error}',
    legendTarget: 'Plan (linear)',
  },
  suggest: {
    aria: 'No goals yet',
    title: 'No goals yet',
    sub: 'For example, take over your previous Strava goals – or add your own.',
    run: 'Run {km} km in {year}',
    ride: 'Ride {km} km in {year}',
    yoga: 'Yoga 7× per week',
    takeAll: 'Add all three',
    custom: 'Add your own goal',
  },
  dialog: {
    perWeek: 'per week',
    perMonth: 'per month',
    perYear: 'per year',
    edit: 'Edit goal',
    add: 'Add goal',
    valueError: 'Please enter a target greater than 0.',
    deleteConfirm: 'Really delete this goal?',
    metric: 'Measure',
    sport: 'Sport',
    allSports: 'All sports',
    period: 'Period',
    value: 'Target',
    validFrom: 'Valid from',
    validTo: 'Valid until (optional)',
    titleField: 'Title (optional)',
    titlePlaceholder: 'generated otherwise',
    healthHint: 'Health goals apply per day or night and compare the average of the last 30 days.',
    create: 'Add',
  },
  records: {
    title: 'Fastest efforts (running)',
    sub: 'Fastest stretch over the distance from {runs} runs with streams, not just whole runs · outline = new in {year}',
    dnf: ' · race, not finished',
    fresh: ' · new {year}',
    notYet: 'not run yet',
    recent: '90 days: {value}',
    aria: '{label}: {value} on {date}',
    ariaDnf: ', not finished',
  },
  today: {
    aria: 'Goals',
    title: 'Goals {year}',
    all: 'All',
    add: 'Add goal',
    empty: 'No goals yet. Add yearly and weekly goals with a forecast under “Goals”.',
    done: 'done',
    open: 'open',
  },
  streak: {
    weekFrom: 'Week of {date}',
    cellOpen: '{when}: still open',
    cellOk: '{when}: met',
    cellOkCount: { one: '{when}: met ({count} activity)', other: '{when}: met ({count} activities)' },
    cellNear: '{when}: just missed – below the minimum',
    cellMissed: '{when}: not met',
    cellsAriaDays: 'Last {n} days: {ok} met',
    cellsAriaWeeks: 'Last {n} weeks: {ok} met',
    cellsAriaOpen: ', the current period is open',
    legendCurrent: 'current streak',
    legendEarlier: 'met, earlier streak',
    legendMissed: 'missed',
    legendNear: 'just missed',
    legendTodayOpen: 'open today',
    legendWeekOpen: 'open this week',
    record: 'Record: {n}',
    running: ' (ongoing)',
    before: ' · before {n}',
    noRecord: 'no record yet',
    removeAria: 'Remove {label}',
    lastHit: 'Last hit:',
    since: 'Since:',
    longest: 'Longest ever:',
    near: 'Just missed:',
    nearText: { one: '{count} day below the minimum (last {value} on {date})', other: '{count} days below the minimum (last {value} on {date})' },
    thisWeek: 'This week:',
    haveOf: '{have} of {need}',
    previewNone: 'With this rule there would be no completed period yet.',
    previewLongest: 'With this rule your longest streak would have been {longest} ({range}).',
    previewNow: ' It would currently be running for {now}.',
    previewStrictDay: '{preview} Strict – one missed day ends the streak. Calendar day in local time of the start, treadmill counts.',
    previewStrictPeriod: '{preview} Strict – one missed period ends the streak. Calendar day in local time of the start, treadmill counts.',
    incomplete: 'Please complete the rule.',
    computing: 'Calculating preview …',
    preview: 'Preview: {text}',
    sport: 'Sport',
    anyActivity: 'Any activity',
    rhythm: 'Rhythm',
    everyDay: 'Every day',
    everyWeek: 'Every week',
    perWeek: 'x times per week',
    perWeekAria: 'How often per week',
    atLeast: 'At least',
    mile: '1 mile ({km})',
    distance: 'Distance …',
    duration: 'Duration …',
    noLimit: 'No minimum',
    minDistAria: 'Minimum distance in km',
    minDurAria: 'Minimum duration in minutes',
    counting: 'Counts',
    single: 'a single activity',
    daySum: 'total of the day',
    newStreak: 'New streak',
    create: 'Add',
    createStreak: 'Add streak',
  },
} satisfies Messages['goals'];
